import * as React from "react";
import {useCallback, useMemo} from "react";
import CodeMirror from "@uiw/react-codemirror";
import {html} from "@codemirror/lang-html";
import {oneDark} from "@codemirror/theme-one-dark";
import {Extension} from "@codemirror/state";
import {EditorView, Decoration, DecorationSet, ViewPlugin, ViewUpdate, MatchDecorator} from "@codemirror/view";

// Light theme with subtle styling
const lightTheme = EditorView.theme({
    "&": {
        backgroundColor: "#ffffff",
        color: "#24292f",
    },
    ".cm-content": {
        caretColor: "#24292f",
    },
    ".cm-cursor, .cm-dropCursor": {
        borderLeftColor: "#24292f",
    },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection": {
        backgroundColor: "#b6d7ff",
    },
    ".cm-gutters": {
        backgroundColor: "#f6f8fa",
        color: "#8b949e",
        borderRight: "1px solid #d0d7de",
    },
    ".cm-activeLineGutter": {
        backgroundColor: "#eaeef2",
    },
    ".cm-activeLine": {
        backgroundColor: "#f6f8fa",
    },
}, {dark: false});

// Mustache variable highlighting ({{variableName}})
const mustacheVariableMark = Decoration.mark({
    class: "cm-mustache-variable"
});

const mustacheDecorator = new MatchDecorator({
    regexp: /\{\{[a-zA-Z0-9_.]+\}\}/g,
    decoration: mustacheVariableMark,
});

const mustacheHighlighter = ViewPlugin.fromClass(class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
        this.decorations = mustacheDecorator.createDeco(view);
    }

    update(update: ViewUpdate) {
        this.decorations = mustacheDecorator.updateDeco(update, this.decorations);
    }
}, {
    decorations: v => v.decorations,
});

// CSS for mustache highlighting
const mustacheStyles = EditorView.baseTheme({
    ".cm-mustache-variable": {
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        padding: "1px 2px",
        borderRadius: "2px",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
    }
});

type CodeMirrorEditorProps = {
    value: string;
    onChange: (value: string) => void;
    language?: "html" | "css" | "javascript" | "text";
    theme?: "dark" | "light";
    height?: string;
    readOnly?: boolean;
}

const CodeMirrorEditor: React.FC<CodeMirrorEditorProps> = ({
    value,
    onChange,
    language = "html",
    theme = "dark",
    height = "400px",
    readOnly = false,
}) => {
    const handleChange = useCallback((val: string) => {
        onChange(val);
    }, [onChange]);

    const extensions = useMemo(() => {
        const exts: Extension[] = [];

        if (language === "html") {
            exts.push(html());
        }

        // Add Mustache highlighting for text mode
        if (language === "text") {
            exts.push(mustacheHighlighter);
            exts.push(mustacheStyles);
        }

        return exts;
    }, [language]);

    const selectedTheme = theme === "dark" ? oneDark : lightTheme;

    return (
        <CodeMirror
            value={value}
            height={height}
            theme={selectedTheme}
            extensions={extensions}
            onChange={handleChange}
            readOnly={readOnly}
            basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightSpecialChars: true,
                foldGutter: true,
                drawSelection: true,
                dropCursor: true,
                allowMultipleSelections: true,
                indentOnInput: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                rectangularSelection: true,
                crosshairCursor: false,
                highlightActiveLine: true,
                highlightSelectionMatches: true,
                closeBracketsKeymap: true,
                searchKeymap: true,
                foldKeymap: true,
                completionKeymap: true,
                lintKeymap: true,
            }}
        />
    );
}

export default CodeMirrorEditor;
